import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { navLinks, allArticleLinks } from '@/mocks/siteData';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const textClass = scrolled
    ? 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
    : 'text-white hover:text-white hover:bg-white/10';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-gradient-to-b from-black/45 via-black/20 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-white font-bold text-sm">
              BS
            </div>
            <span className={`font-heading text-lg md:text-xl font-semibold transition-colors duration-300 ${
              scrolled ? 'text-foreground-900' : 'text-white drop-shadow-md'
            }`}>
              Basetao Sheet
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${textClass}`}
            >
              Home
            </Link>

            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${textClass}`}
              >
                {link.label}
              </Link>
            ))}

            <div className="relative">
              <button
                onClick={() => setArticlesOpen(!articlesOpen)}
                className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer ${textClass}`}
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${articlesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {articlesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-xl shadow-xl z-50 py-2 max-h-80 overflow-y-auto">
                  {allArticleLinks.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setArticlesOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-500 shrink-0">
                        <i className={`${article.icon} text-sm`}></i>
                      </div>
                      <span className="truncate">{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Explore Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            >
              Home
            </Link>
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                {link.label}
              </Link>
            ))}

            <div className="mt-2">
              <button
                onClick={() => setMobileArticlesOpen(!mobileArticlesOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              >
                <span>Shopping Guide</span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileArticlesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {mobileArticlesOpen && (
                <div className="mt-1 grid grid-cols-1 gap-0.5 pl-2">
                  {allArticleLinks.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className={`${article.icon} text-primary-500`}></i>
                      <span>{article.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="col-span-2 px-4 py-3 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors text-center cursor-pointer"
              >
                Explore Spreadsheet
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}